import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowRight,
  Loader2,
  Sparkles,
  Wand2,
  BookOpen,
  History,
  LogOut,
  Flame,
  Clock,
  Award,
  ChefHat,
} from "lucide-react";
import {
  recipeApi,
  getToken,
  getStoredUser,
  clearSession,
  RECIPE_CATEGORIES,
} from "@/lib/recipeApi";
import RecipeDetail from "./RecipeDetail";

const TABS = [
  { key: "generate", label: "AI Generator", icon: Wand2 },
  { key: "healthy", label: "Make It Healthy", icon: Sparkles },
  { key: "library", label: "Recipe Library", icon: BookOpen },
  { key: "history", label: "My Recipes", icon: History },
];

const RecipesDashboardPage = () => {
  const nav = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const [tab, setTab] = useState("generate");
  const [openRecipe, setOpenRecipe] = useState(null);

  useEffect(() => {
    if (!getToken()) {
      nav("/recipes/login");
      return;
    }
    recipeApi
      .me()
      .then(({ data }) => setUser(data))
      .catch(() => {
        clearSession();
        nav("/recipes/login");
      });
  }, [nav]);

  const logout = () => {
    clearSession();
    nav("/recipes/login");
  };

  return (
    <div className="bg-[#0A0A0A] min-h-screen text-white pb-24" data-testid="recipes-dashboard">
      <div className="pt-24 sm:pt-28 pb-6 border-b border-[#1A1A1A] bg-black/40 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between gap-4 mb-4 sm:mb-5">
            <div>
              <p className="text-[#D4AF37] text-[10px] tracking-[0.35em] uppercase font-bold">
                Camp Cormier · Recipe AI
              </p>
              <p className="text-white/50 text-xs mt-1" data-testid="user-email">
                {user?.email}
              </p>
            </div>
            <Button
              onClick={logout}
              variant="ghost"
              size="sm"
              className="text-white/60 hover:text-white hover:bg-white/5 rounded-none"
              data-testid="logout-btn"
            >
              <LogOut size={14} className="mr-2" />
              Log out
            </Button>
          </div>

          <div className="flex gap-1 sm:gap-2 overflow-x-auto -mx-4 px-4" data-testid="dashboard-tabs">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => {
                  setTab(t.key);
                  setOpenRecipe(null);
                }}
                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2.5 text-[11px] tracking-[0.2em] uppercase font-bold transition-all border-b-2 ${
                  tab === t.key
                    ? "border-[#D4AF37] text-[#D4AF37]"
                    : "border-transparent text-white/60 hover:text-white"
                }`}
                data-testid={`tab-${t.key}`}
              >
                <t.icon size={13} />
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 sm:pt-10">
        {openRecipe ? (
          <RecipeDetail recipe={openRecipe} onBack={() => setOpenRecipe(null)} />
        ) : (
          <>
            {tab === "generate" && <GeneratorPanel onCreated={setOpenRecipe} />}
            {tab === "healthy" && <MakeItHealthyPanel onCreated={setOpenRecipe} />}
            {tab === "library" && <LibraryPanel onOpen={setOpenRecipe} />}
            {tab === "history" && <HistoryPanel onOpen={setOpenRecipe} />}
          </>
        )}
      </div>
    </div>
  );
};

const GeneratorPanel = ({ onCreated }) => {
  const [f, setF] = useState({
    prompt: "",
    goal: "muscle_gain",
    meal_type: "",
    cuisine: "",
    calories: "",
    protein_g: "",
    ingredients_on_hand: "",
    dietary_restrictions: "",
    allergies: "",
    cooking_time_max: "",
    servings: 2,
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.prompt.trim().length < 3) {
      toast.error("Describe the meal you want");
      return;
    }
    setLoading(true);
    try {
      const body = {
        ...f,
        calories: f.calories ? Number(f.calories) : null,
        protein_g: f.protein_g ? Number(f.protein_g) : null,
        cooking_time_max: f.cooking_time_max ? Number(f.cooking_time_max) : null,
        servings: Number(f.servings) || 2,
      };
      const { data } = await recipeApi.generate(body);
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const CUISINES = ["Mexican", "Italian", "American", "Japanese", "Korean", "Mediterranean", "Indian", "Thai", "French"];

  return (
    <div data-testid="panel-generate">
      <PanelHeading
        eyebrow="AI Recipe Generator"
        title="TELL AI WHAT YOU WANT"
        body="Describe the meal, set your macros, and we'll return a full recipe with a photorealistic image in seconds."
      />
      <form onSubmit={submit} className="border border-[#1A1A1A] bg-black/60 p-5 sm:p-8 space-y-5">
        <Field label="What are you craving? *">
          <Textarea
            value={f.prompt}
            onChange={(e) => set("prompt", e.target.value)}
            placeholder="e.g. spicy Korean-style chicken bowl with rice and kimchi under 550 calories"
            className="premium-textarea min-h-[100px] rounded"
            data-testid="gen-prompt"
          />
        </Field>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Goal">
            <select value={f.goal} onChange={(e) => set("goal", e.target.value)} className="premium-input rounded w-full" data-testid="gen-goal">
              <option value="">—</option>
              <option value="fat_loss">Fat Loss</option>
              <option value="muscle_gain">Muscle Gain</option>
              <option value="maintenance">Maintenance</option>
              <option value="recomp">Body Recomp</option>
              <option value="contest_prep">Contest Prep</option>
            </select>
          </Field>
          <Field label="Meal Type">
            <select value={f.meal_type} onChange={(e) => set("meal_type", e.target.value)} className="premium-input rounded w-full" data-testid="gen-meal-type">
              <option value="">—</option>
              <option value="breakfast">Breakfast</option>
              <option value="lunch">Lunch</option>
              <option value="dinner">Dinner</option>
              <option value="snack">Snack</option>
              <option value="dessert">Dessert</option>
              <option value="drink">Drink</option>
            </select>
          </Field>
          <Field label="Cuisine">
            <select value={f.cuisine} onChange={(e) => set("cuisine", e.target.value)} className="premium-input rounded w-full" data-testid="gen-cuisine">
              <option value="">—</option>
              {CUISINES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Field label="Calories"><Input type="number" value={f.calories} onChange={(e) => set("calories", e.target.value)} placeholder="500" className="premium-input rounded" data-testid="gen-calories" /></Field>
          <Field label="Protein (g)"><Input type="number" value={f.protein_g} onChange={(e) => set("protein_g", e.target.value)} placeholder="40" className="premium-input rounded" data-testid="gen-protein" /></Field>
          <Field label="Cook Time (min)"><Input type="number" value={f.cooking_time_max} onChange={(e) => set("cooking_time_max", e.target.value)} placeholder="30" className="premium-input rounded" data-testid="gen-cook-time" /></Field>
          <Field label="Servings"><Input type="number" min={1} max={20} value={f.servings} onChange={(e) => set("servings", e.target.value)} className="premium-input rounded" data-testid="gen-servings" /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Ingredients you already have"><Input value={f.ingredients_on_hand} onChange={(e) => set("ingredients_on_hand", e.target.value)} placeholder="chicken, rice, spinach…" className="premium-input rounded" data-testid="gen-ingredients" /></Field>
          <Field label="Dietary restrictions"><Input value={f.dietary_restrictions} onChange={(e) => set("dietary_restrictions", e.target.value)} placeholder="gluten-free, keto, vegan…" className="premium-input rounded" data-testid="gen-restrictions" /></Field>
        </div>
        <Field label="Allergies (strict)"><Input value={f.allergies} onChange={(e) => set("allergies", e.target.value)} placeholder="peanuts, shellfish…" className="premium-input rounded" data-testid="gen-allergies" /></Field>
        <Button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-none py-6 text-xs tracking-[0.25em] uppercase font-bold" data-testid="gen-submit">
          {loading ? (<><Loader2 size={14} className="mr-2 animate-spin" />Cooking… (30–60s)</>) : (<>Generate Recipe<ArrowRight size={14} className="ml-2" /></>)}
        </Button>
      </form>
    </div>
  );
};

const HEALTHY_SUGGESTIONS = ["Big Mac", "McDonald's Fries", "Chick-fil-A Sandwich", "Chipotle Burrito", "Domino's Pizza", "Cheesecake Factory Alfredo", "Starbucks Frappuccino", "Cinnabon Cinnamon Roll", "Krispy Kreme Donut", "Oreo Cheesecake", "Buffalo Wings", "Sushi Roll", "Tres Leches Cake", "Chocolate Brownie"];

const MakeItHealthyPanel = ({ onCreated }) => {
  const [food, setFood] = useState("");
  const [goal, setGoal] = useState("");
  const [servings, setServings] = useState(1);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (food.trim().length < 2) { toast.error("Type the meal to recreate"); return; }
    setLoading(true);
    try {
      const { data } = await recipeApi.makeItHealthy({ food_name: food.trim(), goal: goal.trim() || null, servings: Number(servings) || 1, generate_image: true });
      onCreated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not recreate that meal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="panel-healthy">
      <PanelHeading eyebrow="Signature Feature" title="MAKE IT HEALTHY" body="Type any restaurant meal, fast food item, dessert, or homemade recipe — AI recreates a macro-friendly version with fewer calories and more protein." />
      <form onSubmit={submit} className="border border-[#1A1A1A] bg-black/60 p-5 sm:p-8 space-y-5">
        <Field label="Meal to recreate *"><Input value={food} onChange={(e) => setFood(e.target.value)} placeholder="e.g. Chipotle Chicken Burrito Bowl" className="premium-input rounded text-base" data-testid="healthy-food-input" /></Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Goal (optional)"><Input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="lower calories · higher protein · keto…" className="premium-input rounded" data-testid="healthy-goal-input" /></Field>
          <Field label="Servings"><Input type="number" min={1} max={20} value={servings} onChange={(e) => setServings(e.target.value)} className="premium-input rounded" data-testid="healthy-servings-input" /></Field>
        </div>
        <div>
          <p className="text-white/50 text-[11px] tracking-wider uppercase mb-2">Quick picks</p>
          <div className="flex flex-wrap gap-2" data-testid="healthy-quick-picks">
            {HEALTHY_SUGGESTIONS.map((s) => (
              <button type="button" key={s} onClick={() => setFood(s)} className="px-3 py-1.5 border border-white/10 text-white/70 hover:border-[#D4AF37]/60 hover:text-[#D4AF37] text-xs transition-colors" data-testid={`quick-pick-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{s}</button>
            ))}
          </div>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-[#D4AF37] text-black hover:bg-white rounded-none py-6 text-xs tracking-[0.25em] uppercase font-bold" data-testid="healthy-submit">
          {loading ? (<><Loader2 size={14} className="mr-2 animate-spin" />Recreating… (30–60s)</>) : (<>Recreate It Healthy<ArrowRight size={14} className="ml-2" /></>)}
        </Button>
      </form>
    </div>
  );
};

const LibraryPanel = ({ onOpen }) => {
  const [cat, setCat] = useState("");
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    recipeApi.library(cat || undefined, 60).then(({ data }) => setRecipes(data)).catch(() => setRecipes([])).finally(() => setLoading(false));
  }, [cat]);

  return (
    <div data-testid="panel-library">
      <PanelHeading eyebrow="Curated Library" title="100+ HAND-CRAFTED RECIPES" body="High-protein, macro-friendly recipes across every category. Tap any recipe to expand full macros, ingredients, instructions, and meal-prep tips." />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="library-filters">
        <FilterPill label="All" active={cat === ""} onClick={() => setCat("")} testid="filter-all" />
        {RECIPE_CATEGORIES.map((c) => <FilterPill key={c.key} label={c.label} active={cat === c.key} onClick={() => setCat(c.key)} testid={`filter-${c.key}`} />)}
      </div>
      {loading ? <CenteredSpinner label="Loading recipes…" /> : recipes.length === 0 ? <EmptyState title="No recipes yet" body="The library is being cooked. Check back shortly or ask the AI Generator to create one for you." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="library-grid">
          {recipes.map((r) => <RecipeCard key={r.id} recipe={r} onClick={() => onOpen(r)} />)}
        </div>
      )}
    </div>
  );
};

const HistoryPanel = ({ onOpen }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    recipeApi.myRecipes(50).then(({ data }) => setItems(data)).catch(() => setItems([])).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="panel-history">
      <PanelHeading eyebrow="Your Kitchen" title="MY RECIPES" body="Everything you've generated with AI is saved here — including your Make It Healthy recreations." />
      {loading ? <CenteredSpinner /> : items.length === 0 ? <EmptyState title="Nothing here yet" body="Head to the AI Generator or Make It Healthy tab to create your first recipe." /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5" data-testid="history-grid">
          {items.map((r) => <RecipeCard key={r.id} recipe={r} onClick={() => onOpen(r)} showKind />)}
        </div>
      )}
    </div>
  );
};

const RecipeCard = ({ recipe, onClick, showKind = false }) => {
  const m = recipe.macros_per_serving || {};
  return (
    <button type="button" onClick={onClick} className="text-left border border-[#1A1A1A] hover:border-[#D4AF37]/50 bg-black/60 overflow-hidden group transition-all hover:-translate-y-1 hover:shadow-[0_15px_40px_-15px_rgba(212,175,55,0.35)]" data-testid={`recipe-card-${recipe.id}`}>
      <div className="aspect-[4/3] bg-black/80 overflow-hidden relative">
        {recipe.image_data_url ? (
          <img src={recipe.image_data_url} alt={recipe.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><ChefHat size={40} className="text-white/20" /></div>
        )}
        {showKind && recipe.kind === "make_it_healthy" && (
          <div className="absolute top-2 left-2 bg-[#25D366] text-white px-2 py-1 text-[9px] tracking-wider uppercase font-bold">Healthy Version</div>
        )}
      </div>
      <div className="p-4">
        <p className="text-white text-sm font-semibold leading-tight mb-2 line-clamp-2">{recipe.title}</p>
        <div className="flex flex-wrap gap-3 text-[11px] text-white/60">
          <span className="flex items-center gap-1"><Flame size={11} className="text-[#D4AF37]" />{m.calories || "—"} kcal</span>
          <span className="flex items-center gap-1"><Award size={11} className="text-[#D4AF37]" />{m.protein_g || "—"}g P</span>
          <span className="flex items-center gap-1"><Clock size={11} className="text-[#D4AF37]" />{(recipe.prep_minutes || 0) + (recipe.cook_minutes || 0)}m</span>
        </div>
      </div>
    </button>
  );
};

const PanelHeading = ({ eyebrow, title, body }) => (
  <div className="mb-6 sm:mb-8">
    <p className="text-[#D4AF37] text-[11px] tracking-[0.4em] uppercase mb-3">{eyebrow}</p>
    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[0.95] mb-3 tracking-tight" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{title}</h1>
    {body && <p className="text-white/60 text-sm sm:text-base max-w-2xl leading-relaxed">{body}</p>}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <Label className="text-[11px] tracking-[0.2em] uppercase text-white/60 font-semibold mb-2 block">{label}</Label>
    {children}
  </div>
);

const FilterPill = ({ label, active, onClick, testid }) => (
  <button onClick={onClick} className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-bold border transition-all ${active ? "border-[#D4AF37] bg-[#D4AF37]/[0.1] text-[#D4AF37]" : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"}`} data-testid={testid}>{label}</button>
);

const CenteredSpinner = ({ label = "Loading…" }) => (
  <div className="flex flex-col items-center py-16 gap-3 text-white/50" data-testid="loading">
    <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
    <span className="text-xs tracking-wider uppercase">{label}</span>
  </div>
);

const EmptyState = ({ title, body }) => (
  <div className="text-center py-16 border border-dashed border-white/10" data-testid="empty-state">
    <ChefHat size={40} className="mx-auto text-white/20 mb-4" />
    <p className="text-white font-semibold mb-1">{title}</p>
    <p className="text-white/50 text-sm max-w-md mx-auto">{body}</p>
  </div>
);

export default RecipesDashboardPage;
